import { Slot } from '@radix-ui/react-slot'
import { useControllableState } from '@radix-ui/react-use-controllable-state'
import clsx from 'clsx'
import React, { type ComponentProps, useCallback, useEffect, useId, useRef } from 'react'

import { FocusTrap } from '../FocusTrap'
import { Popper } from '../Popper'
import { Presence } from '../Presence'

interface TriggerProps {
  open: boolean;
}

interface PopoverProps {
  align?: 'start' | 'center' | 'end';
  children: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  portalContainer?: Element;
  positionStrategy?: 'absolute' | 'fixed';
  setInitialFocusOn?: ComponentProps<typeof FocusTrap>['setInitialFocusOn'];
  side?: 'top' | 'right' | 'bottom' | 'left';
  trigger: React.ReactNode | (({ open }: TriggerProps) => React.ReactNode);
  withPortal?: boolean;
  width?: 'auto' | 'triggerWidth';
  zIndex?: number;
}

function MaybePortal ({
  children,
  portalContainer,
  withPortal,
}: {
  children: React.ReactNode;
  portalContainer?: Element;
  withPortal: boolean;
}) {
  if (withPortal) {
    return (
      <Popper.Portal portalContainer={portalContainer}>
        {children}
      </Popper.Portal>
    )
  }

  return children
};

export function Popover ({
  align = 'center',
  children,
  open: openProp,
  defaultOpen = false,
  setInitialFocusOn,
  onOpenChange,
  portalContainer,
  positionStrategy = 'absolute',
  side = 'bottom',
  trigger,
  width = 'auto',
  withPortal = true,
  zIndex,
}: PopoverProps) {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  })
  const contentId = useId()
  const anchorRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const anchor = anchorRef.current
    const content = contentRef.current

    const handleInteractOutside = (event: MouseEvent | FocusEvent) => {
      const clickedInAnchor = anchor?.contains(event.target as Node)
      const clickedInContent = content?.contains(event.target as Node)

      if (!(clickedInAnchor || clickedInContent)) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleInteractOutside)
      document.addEventListener('focusin', handleInteractOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleInteractOutside)
      document.removeEventListener('focusin', handleInteractOutside)
    }
  }, [open])

  useEffect(() => {
    if (open) {
      function handleKeyDown (event: KeyboardEvent) {
        if (event.key === 'Escape') {
          setOpen(false)
        }
      }

      document.addEventListener('keydown', handleKeyDown)

      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const toggleOpen = useCallback(() => {
    setOpen((prevOpen) => !prevOpen)
  }, [setOpen])

  function renderTrigger () {
    if (typeof trigger === 'function') {
      return trigger({ open })
    }

    return trigger
  }

  return (
    <Popper>
      <Popper.Anchor asChild>
        <Slot
          aria-haspopup='dialog'
          aria-expanded={open}
          aria-controls={contentId}
          data-state={open ? 'open' : 'closed'}
          onClick={toggleOpen}
          ref={anchorRef}
        >
          {renderTrigger()}
        </Slot>
      </Popper.Anchor>
      <MaybePortal portalContainer={portalContainer} withPortal={withPortal}>
        <Presence asChild present={open}>
          <Popper.Content
            align={align}
            className={clsx(
              'rounded-lg border border-gray-200 bg-white shadow data-[side=top]:animate-slide-up-and-fade-in data-[side=bottom]:animate-slide-down-and-fade-in data-[side=left]:animate-slide-left-and-fade-in data-[side=right]:animate-slide-right-and-fade-in',
              'max-w-[--popper-available-width]',
              {
                'w-[--popper-anchor-width]': width === 'triggerWidth',
              }
            )}
            id={contentId}
            ref={contentRef}
            role='dialog'
            positionStrategy={positionStrategy}
            side={side}
            sideOffset={4}
            zIndex={zIndex}
          >
            <FocusTrap
              active={open}
              className='rounded-lg outline outline-2 outline-transparent focus-visible:outline-thatch-500'
              setInitialFocusOn={setInitialFocusOn}
            >
              {children}
            </FocusTrap>
          </Popper.Content>
        </Presence>
      </MaybePortal>
    </Popper>
  )
}
